'use client'
import contactUs from '@/app/api/contact'
import LoadingSpinner from '@/app/components/common/LoadingSpinner'
import Title from '@/app/components/common/Title'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

export const ContactUsForm = () => {
	const t = useTranslations('ContactUs')
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	const [recaptchaKey, setRecaptchaKey] = useState('')
	const [showSuccessMessage, setShowSuccessMessage] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState('')
	const [state, setState] = useState({
		name: '',
		email: '',
		subject: '',
		message: '',
	})

	const { executeRecaptcha } = useGoogleReCaptcha()

	const handleReCaptchaVerify = useCallback(async () => {
		if (!executeRecaptcha) {
			console.log('Execute recaptcha not yet available')
			return
		}

		if (recaptchaKey?.length) return

		const token = await executeRecaptcha(
			process.env.NEXT_PUBLIC_RECAPTCHA_ACTION as string
		)

		setRecaptchaKey(token)
	}, [executeRecaptcha])

	useEffect(() => {
		handleReCaptchaVerify()
	}, [handleReCaptchaVerify])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const { name, email, subject, message } = state

		const data = {
			name,
			email,
			subject,
			message,
			'g-recaptcha-response': recaptchaKey as string,
		}

		try {
			setIsSubmitting(true)
			await contactUs(data)
			setRecaptchaKey('')
			setShowSuccessMessage(true)
			setState({
				name: '',
				email: '',
				subject: '',
				message: '',
			})
		} catch (error) {
			setError(
				'An error occurred during contact form submission. Please try again later.'
			)
			console.error('An error occurred during contact form submission:', error)
		} finally {
			setRecaptchaKey('')
			setIsSubmitting(false)
		}
	}

	const onChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target
		setState((prevState) => ({
			...prevState,
			[name]: value,
		}))
	}

	return (
		<div className="container mx-auto mt-8 mb-8">
			{showSuccessMessage ? (
				<div className="min-h-[250px]">
					<Title level="3" className="mb-8">
						{t('successMessage')}
					</Title>
				</div>
			) : (
				<div>
					<div className="mb-8 text-base md:text-xl">{t('content')}</div>
					<form onSubmit={handleSubmit} className="max-w-md">
						<div className="mb-4">
							<label
								htmlFor="name"
								className="block text-sm font-medium text-gray-600"
							>
								{t('name')}*
							</label>
							<input
								type="text"
								id="name"
								name="name"
								className="mt-1 p-2 border border-gray-300 rounded-md w-full sentry-mask"
								onChange={onChange}
								value={state.name}
								required
							/>
						</div>
						<div className="mb-4">
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-600"
							>
								{t('email')}*
							</label>
							<input
								type="email"
								name="email"
								id="email"
								className="mt-1 p-2 border border-gray-300 rounded-md w-full sentry-mask"
								onChange={onChange}
								value={state.email}
								required
								pattern={emailRegex.source}
								title={t('emailError')}
							/>
						</div>
						<div className="mb-4">
							<label
								htmlFor="subject"
								className="block text-sm font-medium text-gray-600"
							>
								{`${t('subject')} (${t('optional')})`}
							</label>
							<input
								type="text"
								id="subject"
								name="subject"
								className="mt-1 p-2 border border-gray-300 rounded-md w-full sentry-mask"
								onChange={onChange}
								value={state.subject}
							/>
						</div>
						<div className="mb-4">
							<label
								htmlFor="message"
								className="block text-sm font-medium text-gray-600"
							>
								{t('yourMessage')}*
							</label>
							<textarea
								id="message"
								name="message"
								className="mt-1 p-2 border border-gray-300 rounded-md w-full sentry-mask"
								rows={4}
								onChange={onChange}
								value={state.message}
								required
							/>
						</div>
						{error && <div className="text-red-500 my-2 text-lg">{error}</div>}

						<button
							type="submit"
							aria-label="Send message"
							disabled={isSubmitting}
							className={classNames(
								'bg-blue-500 min-w-[150px] flex justify-center text-white p-2 rounded-md hover:bg-blue-600 focus:outline-none focus:border-blue-700 focus:ring focus:ring-blue-200 transition duration-300 px-6',
								{
									'opacity-50 cursor-not-allowed': isSubmitting,
								}
							)}
						>
							{isSubmitting ? <LoadingSpinner /> : t('send')}
						</button>
					</form>
				</div>
			)}
		</div>
	)
}
